import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MoreVertical, Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { PageToolbar } from '@/components/layout/PageToolbar'
import { Button } from '@/components/ui/Button'
import { PageError } from '@/components/ui/PageAsyncState'
import { ProtoExcelTable, type ProtoColumn } from '@/components/table/ProtoExcelTable'
import { SupplierStatusBadge } from '@/features/suppliers/SupplierStatusBadge'
import { computeSupplierListKpis } from '@/features/suppliers/supplierListMetrics'
import {
  supplierService,
  supplierFormatService,
  type GoodsCategory,
  type Supplier,
  type SupplierStatus,
} from '@/services/suppliers'

const fieldClass =
  'h-8 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2 text-sm outline-none focus:border-[var(--color-accent)]'

function SupplierRowActionsMenu({
  supplier,
  onDeleted,
}: {
  supplier: Supplier
  onDeleted: () => void
}) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    setOpen(false)
    const confirmed = window.confirm(
      `Xóa NCC "${supplier.name}"? Hành động này không thể hoàn tác.`,
    )
    if (!confirmed) return

    try {
      await supplierService.delete(supplier.id)
      alert('Đã xóa nhà cung cấp thành công.')
      onDeleted()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Xóa NCC thất bại')
    }
  }

  return (
    <div className="relative inline-flex items-center justify-end" ref={menuRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setOpen((prev) => !prev)
        }}
        className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors cursor-pointer"
        title="Thao tác"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-32 origin-top-right rounded-md border border-slate-200 bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
          <button
            type="button"
            onClick={handleDelete}
            className="flex w-full items-center px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
          >
            <Trash2 className="mr-2 h-3.5 w-3.5 text-rose-400" />
            Xóa NCC
          </button>
        </div>
      )}
    </div>
  )
}

type SupplierStatusFilter = SupplierStatus | 'ALL'

export function SupplierListPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<Supplier[]>([])
  const [categories, setCategories] = useState<GoodsCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [categoryFilter, setCategoryFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<SupplierStatusFilter>('ACTIVE')

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await supplierService.list()
      setRows(list)
      await supplierFormatService.fetchFormats()
      setCategories(supplierService.listCategories())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được danh sách NCC')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const kpis = useMemo(() => computeSupplierListKpis(rows), [rows])

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== 'ALL' && r.status !== statusFilter) return false
      if (categoryFilter && !r.category_ids.includes(categoryFilter)) return false
      return true
    })
  }, [rows, statusFilter, categoryFilter])

  const columns: ProtoColumn<Supplier>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Tên NCC',
        sortable: true,
        hideable: false,
        width: 320,
        cell: (r) => <span className="font-medium">{r.name}</span>,
        sortValue: (r) => r.name,
      },
      {
        id: 'category',
        header: 'Nhóm hàng',
        sortable: true,
        cell: (r) => supplierService.categoryLabels(r.category_ids),
        sortValue: (r) => supplierService.categoryLabels(r.category_ids),
      },
      {
        id: 'contact',
        header: 'Người liên hệ',
        sortable: true,
        cell: (r) => r.contact_person || '—',
        sortValue: (r) => r.contact_person ?? '',
      },
      {
        id: 'phone',
        header: 'Điện thoại',
        cell: (r) => r.phone || '—',
        sortValue: (r) => r.phone ?? '',
      },
      {
        id: 'vat',
        header: 'VAT',
        align: 'right',
        sortable: true,
        cell: (r) => (
          <span className="tabular-nums">{r.default_vat_rate.toFixed(2)}%</span>
        ),
        sortValue: (r) => r.default_vat_rate,
      },
      {
        id: 'lead',
        header: 'Lead Time',
        align: 'right',
        sortable: true,
        cell: (r) => (
          <span className="tabular-nums">{r.lead_time_days} ngày</span>
        ),
        sortValue: (r) => r.lead_time_days,
      },
      {
        id: 'pay',
        header: 'Điều khoản',
        sortable: true,
        cell: (r) =>
          supplierService.getPaymentTerm(r.payment_term_id)?.name ?? '—',
        sortValue: (r) =>
          supplierService.getPaymentTerm(r.payment_term_id)?.name ?? '',
      },
      {
        id: 'status',
        header: 'Trạng thái',
        sortable: true,
        cell: (r) => <SupplierStatusBadge status={r.status} />,
        sortValue: (r) => r.status,
      },
      {
        id: 'actions',
        header: '',
        width: 50,
        align: 'center',
        cell: (r) => (
          <SupplierRowActionsMenu
            supplier={r}
            onDeleted={() => void reload()}
          />
        ),
      },
    ],
    [reload],
  )

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Nhà cung cấp' },
        ]}
      />
      <PageHeader
        title="Nhà cung cấp"
        subtitle="Quản lý danh sách nhà cung cấp"
        actions={
          <Button onClick={() => navigate('/purchasing/suppliers/new')}>
            <Plus className="h-4 w-4" />
            Tạo NCC
          </Button>
        }
      />
          <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
            <KpiCard label="Tổng" value={kpis.total} />
            <KpiCard label="Đang hoạt động" value={kpis.active} tone="success" />
            <KpiCard
              label="Ngừng hoạt động"
              value={kpis.inactive}
              tone={kpis.inactive ? 'warning' : 'default'}
            />
          </div>

          {error ? <PageError message={error} onRetry={() => void reload()} /> : null}

          <PageToolbar
            left={
              <>
                <select
                  className={fieldClass}
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  aria-label="Lọc nhóm hàng"
                >
                  <option value="">Tất cả nhóm hàng</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <select
                  className={fieldClass}
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as SupplierStatusFilter)
                  }
                  aria-label="Lọc trạng thái"
                >
                  <option value="ACTIVE">Đang dùng</option>
                  <option value="ALL">Hiện NCC ngừng dùng</option>
                  <option value="INACTIVE">Chỉ NCC ngừng dùng</option>
                </select>
              </>
            }
          />

          <ProtoExcelTable
            showRowNumbers
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            searchPlaceholder="Tìm mã NCC, tên, người liên hệ..."
            onRowClick={(r) => navigate(`/purchasing/suppliers/${r.id}`)}
            exportName="nha-cung-cap"
            pageSize={50}
            loading={loading}
            emptyMessage="Không có nhà cung cấp phù hợp bộ lọc."
            stickyLeftColumnIds={['name']}
          />
    </div>
  )
}
